/**
 * Guest meeting invite — unauthenticated page at /room/:joinCode.
 *
 * Flow: lookup the room by join_code → if a host is present, render the
 * name+company form; on submit mint a guest LiveKit token and hand off to
 * video-frontend via sessionStorage + full-page nav. If no host is present,
 * show the blocking "no host" view (AC6). One-shot check on page load.
 */

import React, { useEffect, useState } from "react";

import {
  guestRoomService,
  type GuestJoinResponse,
  type GuestRoomInfo,
} from "../api/video.api";
import { signInHref } from "../modules/call/components/PreJoinView";
import { InAppBrowserGate } from "../modules/call/components/InAppBrowserGate";
import { useShellStore } from "../store";

const GUEST_SESSION_STORAGE_KEY = "weco.guestSession";

type GuestSession = GuestJoinResponse & { joinCode: string; ts: number };

function storeGuestSession(joinCode: string, response: GuestJoinResponse) {
  const session: GuestSession = { ...response, joinCode, ts: Date.now() };
  sessionStorage.setItem(GUEST_SESSION_STORAGE_KEY, JSON.stringify(session));
}

const inputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
  e.currentTarget.style.borderColor = "var(--accent)";
  e.currentTarget.style.boxShadow = "0 0 0 3px rgba(var(--accent-rgb),0.15)";
};
const inputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
  e.currentTarget.style.borderColor = "var(--border)";
  e.currentTarget.style.boxShadow = "none";
};

interface GuestMeetingPageProps {
  joinCode: string;
}

export const GuestMeetingPage: React.FC<GuestMeetingPageProps> = ({ joinCode }) => {
  // A signed-in visitor is not a guest: they join under their account and
  // skip the name/company form (GH-1915).
  const signedIn = useShellStore((s) => s.isAuthenticated);
  const accountName = useShellStore((s) => s.user?.name?.trim() || s.user?.email || "");

  const [lookupLoading, setLookupLoading] = useState(true);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [room, setRoom] = useState<GuestRoomInfo | null>(null);

  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLookupLoading(true);
    guestRoomService
      .lookup(joinCode)
      .then((info) => {
        if (cancelled) return;
        setRoom(info);
        setLookupError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const status = (err as { code?: number })?.code;
        setLookupError(
          status === 404
            ? "This invite link is invalid or has expired."
            : "Could not reach the meeting service. Try again in a moment."
        );
      })
      .finally(() => {
        if (!cancelled) setLookupLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [joinCode]);

  const trimmedName = name.trim();
  const trimmedCompany = company.trim();
  const canSubmit =
    !submitLoading &&
    (signedIn || (trimmedName.length > 0 && trimmedCompany.length > 0));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitError(null);
    setSubmitLoading(true);
    try {
      const response = signedIn
        ? await guestRoomService.joinAsAccount(joinCode)
        : await guestRoomService.join(joinCode, {
            name: trimmedName,
            company: trimmedCompany,
          });
      storeGuestSession(joinCode, response);
      // #207: go straight to the SPA's guest call route. The old
      // `/remote/video/room/<code>` (singular) URL only matched nginx's
      // plural `/remote/video/rooms/…` redirect, so it fell through to the
      // catch-all `/remote/video/ → /call/` and dropped the join code.
      window.location.assign(`/call/r/${joinCode}`);
    } catch (err: unknown) {
      const status = (err as { code?: number })?.code;
      setSubmitError(
        status === 403
          ? "The meeting host has left. Ask for a new link."
          : status === 404
            ? "This invite link is invalid or has expired."
            : "Could not join the meeting. Try again in a moment."
      );
      setSubmitLoading(false);
    }
  };

  return (
    <InAppBrowserGate fullScreen>
      <div
        className="flex min-h-screen items-center justify-center px-8"
        style={{ background: "var(--bg-root)", fontFamily: "Inter, sans-serif" }}
      >
        <div className="w-full max-w-sm">
          <div className="mb-6 flex justify-center">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-lg"
              style={{ background: "#3366FF", color: "#FFFFFF" }}
            >
              W
            </div>
          </div>

          {lookupLoading && (
            <p
              className="text-sm text-center"
              style={{ color: "var(--text-secondary)" }}
            >
              Checking meeting…
            </p>
          )}

          {!lookupLoading && lookupError && (
            <div className="text-center">
              <h1
                className="text-2xl font-bold mb-2"
                style={{ color: "var(--text-primary)" }}
              >
                Invite unavailable
              </h1>
              <p
                className="text-sm mb-6"
                style={{ color: "var(--text-secondary)" }}
              >
                {lookupError}
              </p>
            </div>
          )}

          {!lookupLoading && room && !room.host_present && (
            <div className="text-center" data-testid="guest-no-host">
              <h1
                className="text-2xl font-bold mb-2"
                style={{ color: "var(--text-primary)" }}
              >
                {room.name}
              </h1>
              <p
                className="text-sm mb-6"
                style={{ color: "var(--text-secondary)" }}
              >
                The meeting host is not in the room yet. Once they join, refresh
                this page to request entry.
              </p>
            </div>
          )}

          {!lookupLoading && room && room.host_present && (
            <form onSubmit={handleSubmit} data-testid="guest-join-form">
              <h1
                className="text-2xl font-bold mb-2 text-center"
                style={{ color: "var(--text-primary)" }}
              >
                Join {room.name}
              </h1>
              <p
                className="text-sm mb-6 text-center"
                style={{ color: "var(--text-secondary)" }}
              >
                {signedIn
                  ? `You'll join as ${accountName}.`
                  : "Enter your name and company to join as a guest."}
              </p>

              {!signedIn && (
              <>
              <label
                className="block text-xs font-semibold mb-2"
                style={{ color: "var(--text-secondary)" }}
              >
                Your name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onFocus={inputFocus}
                onBlur={inputBlur}
                required
                maxLength={100}
                data-testid="guest-name-input"
                className="w-full px-3 py-3 rounded-xl text-sm mb-4"
                style={{
                  background: "var(--bg-surface)",
                  border: "1px solid var(--border)",
                  color: "var(--text-primary)",
                }}
              />

              <label
                className="block text-xs font-semibold mb-2"
                style={{ color: "var(--text-secondary)" }}
              >
                Company
              </label>
              <input
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                onFocus={inputFocus}
                onBlur={inputBlur}
                required
                maxLength={100}
                data-testid="guest-company-input"
                className="w-full px-3 py-3 rounded-xl text-sm mb-6"
                style={{
                  background: "var(--bg-surface)",
                  border: "1px solid var(--border)",
                  color: "var(--text-primary)",
                }}
              />
              </>
              )}

              {submitError && (
                <p
                  className="text-sm mb-4"
                  style={{ color: "var(--danger, #ef4444)" }}
                >
                  {submitError}
                </p>
              )}

              <button
                type="submit"
                disabled={!canSubmit}
                data-testid="guest-join-submit"
                className="inline-flex items-center justify-center w-full py-3 rounded-xl text-sm font-bold"
                style={{
                  background: canSubmit ? "#3366FF" : "var(--bg-surface)",
                  color: canSubmit ? "#FFFFFF" : "var(--text-secondary)",
                  cursor: canSubmit ? "pointer" : "not-allowed",
                  boxShadow: canSubmit
                    ? "0 8px 32px rgba(var(--accent-rgb),0.3)"
                    : "none",
                }}
              >
                {submitLoading ? "Joining…" : "Join meeting"}
              </button>

              {!signedIn && (
                <a
                  href={signInHref()}
                  data-testid="guest-sign-in-link"
                  className="block mt-4 text-sm font-semibold text-center"
                  style={{ color: "var(--accent)" }}
                >
                  Sign in to join with your account
                </a>
              )}
            </form>
          )}
        </div>
      </div>
    </InAppBrowserGate>
  );
};

export default GuestMeetingPage;
